import { Timestamp, where, type QueryConstraint } from 'firebase/firestore';
import type { CompanyMember, MemberRole } from '../types/models';

interface JobScopeArgs {
  activeCompanyId: string | null;
  role: MemberRole | null;
  uid?: string | null;
  memberships: CompanyMember[];
}

interface JobScope {
  constraints: QueryConstraint[];
  blocked: boolean;
  error: string | null;
}

export function buildJobReadConstraints({
  activeCompanyId,
  role,
  uid,
  memberships,
}: JobScopeArgs): JobScope {
  if (!activeCompanyId) {
    return { constraints: [], blocked: true, error: null };
  }

  if (!role) {
    return {
      constraints: [],
      blocked: true,
      error: 'Your company role could not be verified. Please sign in again.',
    };
  }

  if (role === 'driver') {
    if (!uid) {
      return {
        constraints: [],
        blocked: true,
        error: 'Your signed-in user could not be verified.',
      };
    }
    return {
      constraints: [where('assignedUserId', '==', uid)],
      blocked: false,
      error: null,
    };
  }

  if (role === 'subcontractor') {
    const subcontractorId = getActiveSubcontractorId(
      activeCompanyId,
      memberships
    );
    if (!subcontractorId) {
      return {
        constraints: [],
        blocked: true,
        error: 'This subcontractor user is not linked to a subcontractor record.',
      };
    }
    return {
      constraints: [where('assignedSubcontractorId', '==', subcontractorId)],
      blocked: false,
      error: null,
    };
  }

  return { constraints: [], blocked: false, error: null };
}

export function getActiveSubcontractorId(
  activeCompanyId: string | null,
  memberships: CompanyMember[]
): string | null {
  if (!activeCompanyId) return null;
  return (
    memberships.find((membership) => membership.companyId === activeCompanyId)
      ?.subcontractorId ?? null
  );
}

/**
 * PR-46 (Bobby, 27 Oct 2026): a subcontractor sees a finished job (completed or
 * archived) for 48 hours after it was completed. The rules enforce 48 hours on
 * the server's clock (`withinSubcontractorWindow`); queries ask for 47, so a
 * computer clock up to an hour fast still asks for a window the rules can
 * prove. A live query keeps the bound it started with and Firestore re-sends it
 * on reconnect, so pages restart it every `SUBCONTRACTOR_WINDOW_REFRESH_MS`.
 * Every other role gets no constraint.
 */
export const SUBCONTRACTOR_WINDOW_REFRESH_MS = 30 * 60 * 1000;
const SUBCONTRACTOR_QUERY_WINDOW_MS = 47 * 60 * 60 * 1000;

export function finishedJobWindow(role: MemberRole | null): QueryConstraint[] {
  if (role !== 'subcontractor') return [];
  return [where('completedAt', '>', Timestamp.fromMillis(Date.now() - SUBCONTRACTOR_QUERY_WINDOW_MS))];
}
